import merge from 'lodash/merge';

import { createDashHttpClient } from '@/apis/dash.http';
import { env } from '@/env';
import { toUtcQueryDateTime } from '@/utils';

export const http = createDashHttpClient(env.tiktokApiUrl);
export const httpCamelCase = createDashHttpClient(env.tiktokApiUrl, {
  camelizeKeys: true,
});

export function getTikTokAccount({ brandId }, config = {}) {
  return httpCamelCase.get(`/brands/${brandId}/account`, config);
}

export function getTrendingVideoList({ brandId, startDate, endDate, limit }, config = {}) {
  return http.get(
    `/brands/${brandId}/top_videos`,
    merge(
      {
        params: {
          startDate: toUtcQueryDateTime(startDate),
          endDate: toUtcQueryDateTime(endDate),
          limit,
        },
      },
      config,
    ),
  );
}

export function getFollowersDemographics({ brandId, date }, config = {}) {
  return http.get(
    `/brands/${brandId}/demographics`,
    merge(
      {
        params: {
          date: toUtcQueryDateTime(date),
        },
      },
      config,
    ),
  );
}

export function getVideoViews({ brandId, videoId, startDate, endDate, timeScale }, config = {}) {
  return http.get(
    `/brands/${brandId}/videos/${videoId}/views`,
    merge(
      {
        params: {
          startDate: toUtcQueryDateTime(startDate),
          endDate: toUtcQueryDateTime(endDate),
          timeScale,
        },
      },
      config,
    ),
  );
}

export async function getTopKeywords({ brandId, includeKeywords, includeHashtags }, config = {}) {
  return http.get(
    `/brands/${brandId}/ugc/top_keywords`,
    merge(
      {
        params: {
          includeKeywords,
          includeHashtags,
        },
      },
      config,
    ),
  );
}

export async function getAdAccounts({ brandId, fetchApi, connectTiktokAdsToken }, config = {}) {
  return httpCamelCase.get(
    `/marketing/brands/${brandId}/ad_accounts`,
    merge(
      {
        params: {
          fetchApi,
          connectTiktokAdsToken,
        },
      },
      config,
    ),
  );
}

export async function updateAdAccount(
  { brandId, sourceAdvertiserId, isMonitored, connectTiktokAdsToken },
  config = {},
) {
  return httpCamelCase.patch(
    `/marketing/brands/${brandId}/ad_accounts/${sourceAdvertiserId}`,
    { isMonitored, connectTiktokAdsToken },
    config,
  );
}

export async function getAdAccountsAllBrands({ brandIds }, config = {}) {
  return httpCamelCase.put(`/marketing/ad_accounts`, { brand_ids: brandIds }, config);
}

export async function getAdCampaignsAllBrands(
  { brandIds, sourceAdAccountIds, sourceCampaignIds },
  config = {},
) {
  const data = {
    brand_ids: brandIds,
    source_ad_account_ids: sourceAdAccountIds,
    source_campaign_ids: sourceCampaignIds,
  };
  return httpCamelCase.put(`/marketing/ad_campaigns`, data, config);
}

export async function getBrandTTCMAccounts({ brandId, fetchApi }, config = {}) {
  return httpCamelCase.get(
    `/creator_marketplace/brands/${brandId}/ttcm_accounts`,
    merge(
      {
        params: {
          fetchApi,
        },
      },
      config,
    ),
  );
}

export async function getTTCMAccountsForMultipleBrands({ brandIds }, config = {}) {
  return httpCamelCase.put(`/creator_marketplace/ttcm_accounts`, { brand_ids: brandIds }, config);
}

export async function updateCreatorMarketplaceAccount(
  { brandId, sourceTtcmAccountId, isMonitored },
  config = {},
) {
  return httpCamelCase.patch(
    `/creator_marketplace/brands/${brandId}/ttcm_accounts/${sourceTtcmAccountId}`,
    { isMonitored },
    config,
  );
}

export async function getCreatorByHandle({ handle }, config = {}) {
  const url = `/creator_marketplace/creators?handle_name=${handle}`;
  return httpCamelCase.get(url, config);
}
export async function getTrendingHashtags({ categoryName, countryCode, dateRange }, config = {}) {
  return httpCamelCase.get(
    `hashtags/trending`,
    merge(
      {
        params: {
          categoryName,
          countryCode,
          dateRange,
        },
      },
      config,
    ),
  );
}

export async function getHashtagDetails({ hashtagId, countryCode, dateRange }, config = {}) {
  return httpCamelCase.get(
    `hashtags/${hashtagId}/details`,
    merge(
      {
        params: {
          countryCode,
          dateRange,
        },
      },
      config,
    ),
  );
}

export async function getFeedPosts({ url, creatorId, limit }, config = {}) {
  const apiUrl = url ?? `/creator_marketplace/creators/${creatorId}/feed`;
  return httpCamelCase.get(
    apiUrl,
    merge(
      {
        params: {
          limit,
        },
      },
      config,
    ),
  );
}

export async function getCampaignCreatorsStats({ brandId, campaignCode }, config = {}) {
  return httpCamelCase.get(
    `/creator_marketplace/brands/${brandId}/orders/${campaignCode}/stats`,
    {},
    config,
  );
}

export async function getSparkAdsStatus({ brandId, sourceVideoId }, config = {}) {
  return httpCamelCase.get(
    `/creator_marketplace/brands/${brandId}/spark_ads`,
    merge(
      {
        params: {
          sourceVideoId,
        },
      },
      config,
    ),
  );
}

export async function postSyncToAdAccount({ brandId, authCode, sourceAdvertiserId }, config = {}) {
  return httpCamelCase.post(
    `/creator_marketplace/brands/${brandId}/spark_ads/authorize`,
    merge(
      {
        authCode,
        sourceAdvertiserId,
      },
      config,
    ),
  );
}

export async function requestSparkAdsAuthorization(
  { brandId, sourceVideoId, authorizationDays },
  config = {},
) {
  return httpCamelCase.post(
    `/creator_marketplace/brands/${brandId}/spark_ads`,
    {
      sourceVideoId,
      authorizationDays,
    },
    config,
  );
}

export async function createMediaForVideo({ brandId, videoId, handleName }, config = {}) {
  return httpCamelCase.patch(
    `/creator_marketplace/brands/${brandId}/videos/${videoId}`,
    {},
    merge(
      {
        params: {
          handleName,
        },
      },
      config,
    ),
  );
}

export async function getCreatorMarketplaceCategories(
  { brandId, labelType = 'SEARCH' },
  config = {},
) {
  return httpCamelCase.get(
    `/creator_marketplace/brands/${brandId}/categories`,
    merge(
      {
        params: {
          labelType,
        },
      },
      config,
    ),
  );
}

export function getTrendingSounds({ brandId, dateRange, countryCode, genre }, config = {}) {
  return httpCamelCase.get(
    `/trending_sounds`,
    merge(
      {
        params: {
          brandId,
          dateRange,
          countryCode,
          genre,
        },
      },
      config,
    ),
  );
}
